import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "./firebase";
import { optimizeImageForUpload } from "./imageUploadUtils";
import { sendUserNotification } from "./notificationApi";
import { assertStorageReady, formatUploadError } from "./uploadErrorUtils";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const PRODUCT_CATEGORIES = [
  "Food",
  "Treats",
  "Toys",
  "Medicines",
  "Accessories",
  "Grooming",
  "Health Supplements",
  "Other",
];

const ORDER_STATUS_OPTIONS = ["placed", "confirmed", "packed", "shipped", "delivered", "cancelled"];

const currency = (value) => `₹${Number(value || 0).toFixed(2)}`;

function statusTone(status) {
  const key = String(status || "").toLowerCase();
  if (key === "delivered") return "success";
  if (key === "shipped" || key === "confirmed") return "accent";
  if (key === "cancelled") return "danger";
  if (key === "packed") return "warning";
  return "default";
}

export default function ProviderMarketplace({ providerId, email }) {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [savingProduct, setSavingProduct] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadHint, setUploadHint] = useState("");
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const [stockDraftById, setStockDraftById] = useState({});
  const [editingProductId, setEditingProductId] = useState("");
  const [editDraft, setEditDraft] = useState({
    title: "",
    category: "Food",
    price: "",
    description: "",
  });
  const [form, setForm] = useState({
    title: "",
    category: "Food",
    price: "",
    stockQty: "",
    description: "",
    imageUrls: [],
  });

  const uploadImageFile = async (file) => {
    const uploadUid = String(providerId || auth.currentUser?.uid || "");
    if (!uploadUid) throw new Error("Login session expired. Please login again.");
    await assertStorageReady(storage?.app?.options?.storageBucket || "");
    const optimized = await optimizeImageForUpload(file, {
      maxWidth: 1600,
      maxHeight: 1600,
      quality: 0.82,
      maxOutputBytes: 900 * 1024,
      skipBelowBytes: 220 * 1024,
      outputType: "image/jpeg",
    });
    if (!optimized || !String(optimized.type || "").startsWith("image/")) {
      throw new Error("Only image files are supported.");
    }
    if ((Number(optimized.size) || 0) > 12 * 1024 * 1024) {
      throw new Error("Image is too large after optimization. Please use a smaller image.");
    }
    const safeName = String(optimized.name || file.name || "image")
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, "-")
      .slice(0, 80);
    const filePath = `marketplaceProducts/${uploadUid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
    const fileRef = ref(storage, filePath);
    await uploadBytes(fileRef, optimized, {
      contentType: optimized.type || file.type || "image/jpeg",
      cacheControl: "public,max-age=3600",
    });
    return getDownloadURL(fileRef);
  };

  const processPickedFiles = async (filesInput = []) => {
    const files = Array.from(filesInput || []);
    if (!files.length) {
      setUploadHint("No file selected.");
      return;
    }
    setUploadHint(`${files.length} file(s) selected.`);
    setSelectedFileNames(files.map((item) => item.name).slice(0, 4));
    setUploadError("");
    const maxRawBytes = 30 * 1024 * 1024;
    const nonImages = files.filter((item) => !String(item.type || "").startsWith("image/")).length;
    const tooLargeRaw = files.filter((item) => Number(item.size) > maxRawBytes).length;
    const selected = files
      .filter((item) => String(item.type || "").startsWith("image/") && Number(item.size) <= maxRawBytes)
      .slice(0, Math.max(0, 4 - form.imageUrls.length));
    if (!selected.length) {
      if (nonImages > 0 || tooLargeRaw > 0) {
        const parts = [];
        if (nonImages > 0) parts.push(`${nonImages} non-image file(s)`);
        if (tooLargeRaw > 0) parts.push(`${tooLargeRaw} file(s) above 30MB`);
        alert(`Upload skipped: ${parts.join(", ")}.`);
      }
      return;
    }
    try {
      setUploading(true);
      const urls = await Promise.all(selected.map(uploadImageFile));
      setForm((prev) => ({ ...prev, imageUrls: [...prev.imageUrls, ...urls].slice(0, 4) }));
      setUploadHint(`Uploaded ${urls.length} image(s).`);
      setSelectedFileNames([]);
    } catch (error) {
      const message = formatUploadError(error, storage?.app?.options?.storageBucket);
      setUploadError(message);
      alert(`Product image upload failed: ${message}`);
    } finally {
      setUploading(false);
    }
  };

  const onPickImages = async (event) => {
    const inputEl = event.currentTarget;
    await processPickedFiles(inputEl?.files || []);
    if (inputEl) inputEl.value = "";
  };

  const loadData = useCallback(async () => {
    if (!providerId) return;
    try {
      setLoading(true);
      setLoadError("");
      const productSnap = await getDocs(query(collection(db, "marketplaceProducts"), where("providerId", "==", providerId)));
      const providerOrders = await getDocs(query(collection(db, "marketplaceOrders"), where("providerId", "==", providerId)));
      const rowsProducts = productSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
      const rowsOrders = providerOrders.docs.map((item) => ({ id: item.id, ...item.data() }));

      rowsProducts.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      rowsOrders.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setProducts(rowsProducts);
      setOrders(rowsOrders);
      setStockDraftById(
        Object.fromEntries(rowsProducts.map((item) => [item.id, String(Math.max(0, Number(item.stockQty) || 0))])),
      );
    } catch (error) {
      setLoadError(error.message || "Unable to load marketplace data.");
      setProducts([]);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveProduct = async () => {
    if (!providerId) return;
    const title = String(form.title || "").trim();
    const category = String(form.category || "").trim();
    const description = String(form.description || "").trim();
    const price = Number(form.price);
    const stockQty = Number(form.stockQty);
    if (!title || !category || !description) {
      alert("Please fill title, category and description.");
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      alert("Price must be greater than 0.");
      return;
    }
    if (!Number.isFinite(stockQty) || stockQty < 0) {
      alert("Stock cannot be negative.");
      return;
    }
    try {
      setSavingProduct(true);
      const providerEmail = String(email || "");
      await addDoc(collection(db, "marketplaceProducts"), {
        providerId,
        providerEmail,
        providerEmailLower: providerEmail.toLowerCase(),
        title,
        category,
        description,
        price,
        stockQty: Math.floor(stockQty),
        imageUrls: form.imageUrls,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setForm({
        title: "",
        category: "Food",
        price: "",
        stockQty: "",
        description: "",
        imageUrls: [],
      });
      await loadData();
      alert("Product listed in marketplace.");
    } catch (error) {
      alert(error.message || "Could not save product.");
    } finally {
      setSavingProduct(false);
    }
  };

  const updateProduct = async (product, patch = {}) => {
    try {
      await updateDoc(doc(db, "marketplaceProducts", product.id), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
      await loadData();
    } catch (error) {
      alert(error.message || "Could not update product.");
    }
  };

  const saveStock = async (product) => {
    const nextStock = Math.max(0, Number(stockDraftById[product.id]) || 0);
    await updateProduct(product, { stockQty: Math.floor(nextStock) });
  };

  const startEdit = (product) => {
    if (!product) return;
    setEditingProductId(product.id);
    setEditDraft({
      title: String(product.title || ""),
      category: String(product.category || "Food"),
      price: String(Number(product.price) || 0),
      description: String(product.description || ""),
    });
  };

  const cancelEdit = () => {
    setEditingProductId("");
    setEditDraft({
      title: "",
      category: "Food",
      price: "",
      description: "",
    });
  };

  const saveEdit = async (product) => {
    if (!product) return;
    const title = String(editDraft.title || "").trim();
    const category = String(editDraft.category || "").trim();
    const description = String(editDraft.description || "").trim();
    const price = Number(editDraft.price);
    if (!title || !category || !description) {
      alert("Please fill title, category and description.");
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      alert("Price must be greater than 0.");
      return;
    }
    await updateProduct(product, { title, category, description, price });
    cancelEdit();
  };

  const deleteProduct = async (product) => {
    if (!product) return;
    const ok = window.confirm(`Delete "${product.title || "this product"}"?`);
    if (!ok) return;
    await updateProduct(product, {
      isActive: false,
      isDeleted: true,
      deletedAt: serverTimestamp(),
    });
    if (editingProductId === product.id) cancelEdit();
  };

  const visibleProducts = useMemo(
    () => products.filter((item) => item?.isDeleted !== true),
    [products],
  );

  const updateOrderStatus = async (order, status) => {
    if (!order?.id || !status) return;
    try {
      const patch = { status, updatedAt: serverTimestamp() };
      if (status === "packed") patch.packedAt = serverTimestamp();
      if (status === "shipped") patch.shippedAt = serverTimestamp();
      if (status === "delivered") patch.deliveredAt = serverTimestamp();
      if (status === "cancelled") patch.cancelledAt = serverTimestamp();
      await updateDoc(doc(db, "marketplaceOrders", order.id), patch);
      const buyerEmail = String(order.buyerEmail || "");
      if (buyerEmail) {
        try {
          await sendUserNotification({
            recipientEmail: buyerEmail,
            type: "marketplace_order_update",
            title: "Order Status Updated",
            message: `${order.productTitle || "Product"} status is now ${status}.`,
            orderId: order.id,
          });
        } catch (_) {
          // Notification failure is non-blocking.
        }
      }
      await loadData();
    } catch (error) {
      alert(error.message || "Could not update order.");
    }
  };

  const summary = useMemo(() => {
    const activeProducts = visibleProducts.filter((item) => item.isActive === true).length;
    const openOrders = orders.filter((item) => !["delivered", "cancelled"].includes(String(item.status || "").toLowerCase())).length;
    const deliveredOrders = orders.filter((item) => String(item.status || "").toLowerCase() === "delivered");
    const revenue = deliveredOrders.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
    return {
      totalProducts: visibleProducts.length,
      activeProducts,
      openOrders,
      deliveredOrders: deliveredOrders.length,
      revenue,
    };
  }, [orders, visibleProducts]);

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Marketplace Seller Hub"
        title="Sell Products On Petaar"
        subtitle="List pet products, keep stock current, and process owner orders from one operating console."
        actions={
          <button className="petaar-button" onClick={loadData} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        }
      />

      {loadError ? <div className="petaar-notice error" style={{ marginBottom: 16 }}>{loadError}</div> : null}

      <div className="petaar-stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Products" value={summary.totalProducts} tone="default" meta="All listed items" />
        <StatCard label="Active listings" value={summary.activeProducts} tone="success" meta="Visible and sellable" />
        <StatCard label="Open orders" value={summary.openOrders} tone="warning" meta="Not yet delivered" />
        <StatCard label="Delivered revenue" value={summary.revenue} prefix="₹" decimals={2} tone="accent" meta={`${summary.deliveredOrders} delivered orders`} />
      </div>

      <div className="petaar-split" style={{ alignItems: "start", marginBottom: 24 }}>
        <section className="petaar-card" style={{ display: "grid", gap: 18 }}>
          <div>
            <div className="petaar-kicker">New Product</div>
            <h3 style={{ fontSize: "1.5rem", marginTop: 6 }}>Create marketplace listing</h3>
          </div>
          <div className="petaar-form-grid">
            <input
              placeholder="Product title"
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
            />
            <select value={form.category} onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}>
              {PRODUCT_CATEGORIES.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              placeholder="Price"
              value={form.price}
              onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))}
            />
            <input
              type="number"
              min={0}
              placeholder="Stock quantity"
              value={form.stockQty}
              onChange={(event) => setForm((prev) => ({ ...prev, stockQty: event.target.value }))}
            />
          </div>
          <textarea
            rows={3}
            placeholder="Product description"
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
          />
          <div className="petaar-card" style={{ padding: 18, display: "grid", gap: 12, background: "var(--surface-alt)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div>
                <div className="petaar-kicker">Product Media</div>
                <h4 style={{ fontSize: "1.1rem" }}>Upload up to 4 images</h4>
              </div>
              <label htmlFor="provider-marketplace-image-input" className="petaar-button petaar-button--secondary" style={{ cursor: uploading ? "not-allowed" : "pointer" }}>
                {uploading ? "Uploading..." : "Select Images"}
              </label>
            </div>
            <input
              id="provider-marketplace-image-input"
              type="file"
              accept="image/*"
              multiple
              disabled={uploading}
              onChange={onPickImages}
              style={{ display: "none" }}
            />
            {form.imageUrls.length ? (
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(88px, 1fr))" }}>
                {form.imageUrls.map((url, index) => (
                  <div key={`${url}-${index}`} style={{ position: "relative" }}>
                    <img src={url} alt="product preview" style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 14, border: "1px solid var(--border)" }} />
                    <button
                      onClick={() => setForm((prev) => ({ ...prev, imageUrls: prev.imageUrls.filter((_, itemIndex) => itemIndex !== index) }))}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: "none",
                        background: "rgba(17, 24, 39, 0.82)",
                        color: "#fff",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "var(--muted)" }}>No product images uploaded yet.</div>
            )}
            {selectedFileNames.length ? <div style={{ color: "var(--muted)", fontSize: "0.95rem" }}>Selected: {selectedFileNames.join(", ")}</div> : null}
            {uploadHint ? <div style={{ color: "var(--muted)", fontSize: "0.95rem" }}>{uploadHint}</div> : null}
            {uploadError ? <div className="petaar-notice error">{uploadError}</div> : null}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="petaar-button" onClick={saveProduct} disabled={savingProduct}>
              {savingProduct ? "Saving..." : "List Product"}
            </button>
          </div>
        </section>

        <section className="petaar-card" style={{ display: "grid", gap: 14 }}>
          <div className="petaar-kicker">Listing Guidance</div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8, color: "var(--muted)" }}>
            <li>Use real pack photos, not marketing creatives or posters.</li>
            <li>Keep product descriptions operational: size, usage, ingredients, and care notes.</li>
            <li>Update stock immediately to reduce cancelled orders.</li>
            <li>Move order status as soon as fulfilment changes.</li>
          </ul>
        </section>
      </div>

      <section className="petaar-card" style={{ marginBottom: 24 }}>
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">Products</div>
            <h3 style={{ fontSize: "1.35rem" }}>Your listed products</h3>
          </div>
          <ToneBadge tone="default">{visibleProducts.length} item(s)</ToneBadge>
        </div>

        {loading ? (
          <div className="petaar-grid" style={{ gap: 16 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={`marketplace-product-skeleton-${index}`} className="petaar-skeleton" style={{ height: 220, borderRadius: 18 }} />
            ))}
          </div>
        ) : visibleProducts.length === 0 ? (
          <EmptyState
            icon="🛍️"
            title="No products listed yet"
            description="Create your first product listing so pet owners can start ordering from your catalogue."
          />
        ) : (
          <div className="petaar-grid" style={{ gap: 16 }}>
            {visibleProducts.map((product) => (
              <div key={product.id} className="petaar-list-row">
                <div style={{ display: "grid", gap: 14, gridTemplateColumns: "120px minmax(0, 1fr)" }}>
                  <div style={{ width: 120, aspectRatio: "1 / 1", borderRadius: 18, overflow: "hidden", border: "1px solid var(--border)", background: "var(--surface-alt)" }}>
                    {Array.isArray(product.imageUrls) && product.imageUrls[0] ? (
                      <img src={product.imageUrls[0]} alt={product.title || "Product"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : null}
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                      <div>
                        <h4 style={{ fontSize: "1.2rem" }}>{product.title || "Product"}</h4>
                        <div style={{ marginTop: 6, color: "var(--muted)" }}>{product.category || "General"} · {currency(product.price)}</div>
                      </div>
                      <ToneBadge tone={product.isActive ? "success" : "danger"}>{product.isActive ? "Active" : "Inactive"}</ToneBadge>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <input
                        type="number"
                        min={0}
                        value={stockDraftById[product.id] ?? String(product.stockQty || 0)}
                        onChange={(event) => setStockDraftById((prev) => ({ ...prev, [product.id]: event.target.value }))}
                        style={{ maxWidth: 140 }}
                      />
                      <button className="petaar-button petaar-button--secondary" onClick={() => saveStock(product)}>Save Stock</button>
                      <button className="petaar-button petaar-button--ghost" onClick={() => updateProduct(product, { isActive: !product.isActive })}>
                        {product.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button className="petaar-button petaar-button--ghost" onClick={() => startEdit(product)}>Edit</button>
                      <button className="petaar-button petaar-button--ghost" onClick={() => deleteProduct(product)}>Delete</button>
                    </div>

                    {editingProductId === product.id ? (
                      <div className="petaar-card" style={{ padding: 18, display: "grid", gap: 12, background: "var(--surface-alt)" }}>
                        <div className="petaar-form-grid">
                          <input
                            placeholder="Product title"
                            value={editDraft.title}
                            onChange={(event) => setEditDraft((prev) => ({ ...prev, title: event.target.value }))}
                          />
                          <select value={editDraft.category} onChange={(event) => setEditDraft((prev) => ({ ...prev, category: event.target.value }))}>
                            {PRODUCT_CATEGORIES.map((item) => (
                              <option key={`edit-${product.id}-${item}`} value={item}>{item}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min={1}
                            placeholder="Price"
                            value={editDraft.price}
                            onChange={(event) => setEditDraft((prev) => ({ ...prev, price: event.target.value }))}
                          />
                        </div>
                        <textarea
                          rows={3}
                          placeholder="Description"
                          value={editDraft.description}
                          onChange={(event) => setEditDraft((prev) => ({ ...prev, description: event.target.value }))}
                        />
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <button className="petaar-button" onClick={() => saveEdit(product)}>Save Changes</button>
                          <button className="petaar-button petaar-button--ghost" onClick={cancelEdit}>Cancel</button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="petaar-card">
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">Orders</div>
            <h3 style={{ fontSize: "1.35rem" }}>Marketplace order queue</h3>
          </div>
          <ToneBadge tone="accent">{orders.length} order(s)</ToneBadge>
        </div>

        {loading ? (
          <div className="petaar-grid" style={{ gap: 16 }}>
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={`marketplace-order-skeleton-${index}`} className="petaar-skeleton" style={{ height: 220, borderRadius: 18 }} />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon="📦"
            title="No orders yet"
            description="Incoming marketplace orders will appear here with shipping details and status controls."
          />
        ) : (
          <div className="petaar-grid" style={{ gap: 16 }}>
            {orders.map((order) => (
              <div key={order.id} className="petaar-list-row">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <div>
                    <h4 style={{ fontSize: "1.15rem" }}>{order.productTitle || "Product"}</h4>
                    <div style={{ marginTop: 6, color: "var(--muted)" }}>
                      Qty {order.quantity || 1} · Amount {currency(order.totalAmount)}
                    </div>
                  </div>
                  <ToneBadge tone={statusTone(order.status)}>{order.status || "placed"}</ToneBadge>
                </div>
                <div style={{ marginTop: 12, display: "grid", gap: 8, color: "var(--muted)" }}>
                  <div>Buyer: <strong style={{ color: "var(--text)" }}>{order.buyerName || order.buyerEmail || "Pet owner"}</strong></div>
                  <div>Phone: <strong style={{ color: "var(--text)" }}>{order.buyerPhone || "N/A"}</strong></div>
                  <div>Address: <strong style={{ color: "var(--text)" }}>{order.shippingAddress || "N/A"}</strong></div>
                </div>
                <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <select value={order.status || "placed"} onChange={(event) => updateOrderStatus(order, event.target.value)} style={{ maxWidth: 220 }}>
                    {ORDER_STATUS_OPTIONS.map((item) => (
                      <option key={`${order.id}-${item}`} value={item}>{item}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
